export const HEATING_LAST_YEAR_LABEL = "Heating last year";
export const COOLING_LAST_YEAR_LABEL = "Cooling last year";

const padDatePart = (value) => String(value).padStart(2, "0");

export const formatMonthDay = (date) =>
  `${padDatePart(date.getUTCMonth() + 1)}/${padDatePart(date.getUTCDate())}`;

export const getUtcDayKey = (date) =>
  Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());

/**
 * Returns the instant one calendar year after the given timestamp (UTC), or null when
 * that day does not exist a year later (Feb 29 into a non-leap year).
 */
export const shiftOneYearForward = (timestamp) => {
  const shifted = new Date(timestamp);
  const month = shifted.getUTCMonth();
  shifted.setUTCFullYear(shifted.getUTCFullYear() + 1);
  // Date rolls Feb 29 over into Mar 1, which would pile two days onto one chart point.
  return shifted.getUTCMonth() === month ? shifted : null;
};

/**
 * Merges last year's energy data points into the current chart rows by calendar day.
 * Each last-year point is shifted forward one year and grouped under that day, so the
 * two series line up by date even though the energy queries only return buckets that
 * have data. Days that only exist last year get a row without current values.
 *
 * currentDays: rows with `date`, `dayKey`, `energyUsage` (and optionally `energyCost`).
 * toDisplayUsage: converts kWh to the user's preferred unit.
 * toCost: converts kWh to a cost, or null when no electric rate is configured.
 */
export const buildComparisonChartData = ({
  currentDays,
  lastYearEnergyData,
  toDisplayUsage,
  toCost = null,
}) => {
  const lastYearByDay = new Map();
  lastYearEnergyData.forEach((item) => {
    const shifted = shiftOneYearForward(item.timestamp);
    if (!shifted) {
      return;
    }
    const original = new Date(item.timestamp);
    const dayKey = getUtcDayKey(shifted);
    const entry = lastYearByDay.get(dayKey) || {
      date: formatMonthDay(shifted),
      lastYearDate: `${formatMonthDay(original)}/${original.getUTCFullYear()}`,
      usage: 0,
      cost: 0,
    };
    entry.usage += toDisplayUsage(item.totalKWh);
    entry.cost += toCost ? toCost(item.totalKWh) : 0;
    lastYearByDay.set(dayKey, entry);
  });

  const rowsByDay = new Map(currentDays.map((row) => [row.dayKey, row]));
  lastYearByDay.forEach((entry, dayKey) => {
    rowsByDay.set(dayKey, {
      date: entry.date,
      dayKey,
      ...rowsByDay.get(dayKey),
      lastYearDate: entry.lastYearDate,
      lastYearEnergyUsage: parseFloat(entry.usage.toFixed(2)),
      lastYearEnergyCost: toCost ? parseFloat(entry.cost.toFixed(2)) : undefined,
    });
  });
  return [...rowsByDay.values()].sort((left, right) => left.dayKey - right.dayKey);
};
